import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useCart, apiErrorMessage } from '@/contexts/CartContext';
import { useAuth } from '@/contexts/AuthContext';
import { formatPrice, orderStatuses } from '@/lib/utils';
import { CHECKOUT } from '@/constants/testIds';
import { DELIVERY_FEE } from '@/constants/shop';
import { toast } from 'sonner';
import { Truck, PackageOpen, Ruler } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import axios from 'axios';
import ProductImage from '@/components/ProductImage';
import AddressPicker from '@/components/AddressPicker';
import AddressModal from '@/components/Account/AddressModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MeasureInput = ({ id, label, unit, value, onChange, ...props }) => (
  <div>
    <Label htmlFor={id} className="font-cairo text-xs text-gray-600 mb-1 block">{label}</Label>
    <div className="relative">
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="font-cairo pl-12"
        {...props}
      />
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400 font-cairo pointer-events-none">{unit}</span>
    </div>
  </div>
);

const Checkout = () => {
  const { cart, fetchCart } = useCart();
  const { user, token, fetchUser } = useAuth();
  const { max_open_orders: maxOpenOrders } = useSettings();

  // حد أقصى للطلبات المفتوحة (من الإعدادات): نشوف طلباتها اللي لسه ما وصلتش
  const [openOrders, setOpenOrders] = useState([]);
  useEffect(() => {
    if (!token || !maxOpenOrders) {
      setOpenOrders([]);
      return;
    }
    axios
      .get(`${API}/orders`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setOpenOrders((res.data?.data || []).filter((o) => ['pending', 'confirmed', 'processing', 'shipped'].includes(o.status)));
      })
      .catch(() => {});
  }, [token, maxOpenOrders]);
  const limitReached = !!maxOpenOrders && openOrders.length >= maxOpenOrders;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    notes: '',
    payment_method: 'cod',
    preview_service_requested: false
  });

  // الطول والوزن لكل منتج في السلة: { [cartItemId]: { height, weight } }
  const [measurements, setMeasurements] = useState({});
  const measurableItems = (cart.items || []).filter((item) => item.available);
  const setMeasurement = (itemId, field, value) =>
    setMeasurements((prev) => ({ ...prev, [itemId]: { ...prev[itemId], [field]: value } }));

  // العنوان المختار: الأساسي افتراضياً، وإلا أول عنوان
  const addresses = user?.addresses || [];
  const [addressId, setAddressId] = useState(null);
  useEffect(() => {
    if (addresses.some((a) => a.id === addressId)) return;
    setAddressId((addresses.find((a) => a.is_default) || addresses[0])?.id ?? null);
  }, [addresses, addressId]);

  // إضافة عنوان جديد من نفس الصفحة
  const emptyAddress = { id: null, city_id: '', street: '', building: '', phone: user?.phone || '', details: '', is_default: false };
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [addressSubmitting, setAddressSubmitting] = useState(false);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/cities`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setCities(res.data?.data || []))
      .catch((err) => console.error('Failed to fetch cities:', err));
  }, [token]);

  const openAddressModal = () => {
    setAddressForm(emptyAddress);
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.city_id || !addressForm.street) {
      toast.error('الرجاء إدخال المحافظة والشارع');
      return;
    }
    setAddressSubmitting(true);
    try {
      const response = await axios.post(`${API}/addresses`, addressForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('تم إضافة العنوان بنجاح');
      setIsAddressModalOpen(false);
      await fetchUser();
      // نختار العنوان الجديد تلقائياً
      const newId = response.data?.data?.id;
      if (newId) setAddressId(newId);
    } catch (error) {
      toast.error('حدث خطأ أثناء حفظ العنوان');
      console.error(error);
    } finally {
      setAddressSubmitting(false);
    }
  };

  const subtotal = cart.total_amount || 0;
  const deliveryFee = DELIVERY_FEE;
  const total = subtotal + deliveryFee;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('يجب تسجيل الدخول أولاً');
      navigate('/login');
      return;
    }

    if (cart.items?.length === 0) {
      toast.error('السلة فارغة');
      return;
    }

    if (!addressId) {
      toast.error('الرجاء اختيار عنوان التوصيل');
      return;
    }

    // الطول والوزن إجباريين لكل منتج
    const missing = measurableItems.find((item) => {
      const h = Number(measurements[item.id]?.height);
      const w = Number(measurements[item.id]?.weight);
      return !(h >= 50 && h <= 250 && w >= 20 && w <= 300);
    });
    if (missing) {
      toast.error(`الرجاء إدخال الطول (50–250 سم) والوزن (20–300 كجم) لـ ${missing.name_ar}`);
      document.getElementById(`height-${missing.id}`)?.focus();
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        address_id: addressId,
        payment_method: formData.payment_method,
        notes: formData.notes,
        // الطول والوزن لكل منتج (إجباري)
        measurements: measurableItems.map((item) => ({
          cart_item_id: item.id,
          height: Number(measurements[item.id]?.height),
          weight: Number(measurements[item.id]?.weight),
        })),
        preview_service_requested: formData.preview_service_requested
      };

      const response = await axios.post(`${API}/orders`, orderData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // السيرفر فضّى السلة بعد إنشاء الطلب — نحدّثها في الواجهة
      await fetchCart();
      const order = response.data?.data;
      navigate(`/order-success/${order.order_number}`, { replace: true, state: { order } });
    } catch (error) {
      console.error('Order creation failed:', error);
      toast.error(apiErrorMessage(error, 'فشل إنشاء الطلب'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-8">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[1200px]">
        <h1 className="text-4xl font-tajawal font-bold mb-8">إتمام الطلب</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            {limitReached && (
              <div role="alert" className="mb-6 flex flex-col sm:flex-row sm:items-start gap-4 rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <span className="shrink-0 w-12 h-12 rounded-full bg-white ring-1 ring-amber-200 flex items-center justify-center">
                  <PackageOpen className="w-6 h-6 text-amber-600" />
                </span>
                <div className="flex-1 font-cairo">
                  <p className="font-tajawal font-bold text-brand-black">
                    {maxOpenOrders === 1 ? 'عندك طلب لسا ما وصلك' : `عندك ${openOrders.length} طلبات لسا ما وصلوكِ`}
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5">
                    {maxOpenOrders === 1
                      ? 'بتقدري تطلبي طلب جديد بعد ما تستلميه.'
                      : `الحد الأقصى ${maxOpenOrders} طلبات في نفس الوقت، بتقدري تطلبي طلب جديد بعد ما تستلمي واحد منهم.`}
                  </p>
                  <ul className="mt-2 flex flex-wrap gap-2">
                    {openOrders.map((o) => (
                      <li key={o.id} className="inline-flex items-center gap-1.5 rounded-full bg-white ring-1 ring-amber-200 px-3 py-1 text-xs">
                        <span className="font-mono font-bold" dir="ltr">{o.order_number}</span>
                        <span className="text-gray-500">— {orderStatuses[o.status]}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <Link
                  to="/account?tab=orders"
                  className="shrink-0 inline-flex items-center justify-center h-10 px-5 rounded-full bg-burgundy-500 hover:bg-burgundy-600 text-white text-sm font-cairo font-semibold transition"
                >
                  متابعة طلباتي
                </Link>
              </div>
            )}

            <form data-testid={CHECKOUT.form} onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-tajawal font-bold mb-4">عنوان التوصيل</h2>
                <AddressPicker
                  addresses={addresses}
                  selectedId={addressId}
                  onSelect={setAddressId}
                  onAddNew={openAddressModal}
                />

                <div
                  role="note"
                  className="mt-5 flex items-center gap-3 md:gap-4 rounded-2xl border border-brand-gold/40 bg-gradient-to-l from-brand-gold/15 via-brand-gold/5 to-transparent p-4"
                >
                  <span className="shrink-0 w-11 h-11 rounded-full bg-white shadow-sm ring-1 ring-brand-gold/30 flex items-center justify-center">
                    <Truck className="w-5 h-5 text-burgundy-500" />
                  </span>
                  <div className="flex-1 min-w-0 font-cairo">
                    <p className="font-tajawal font-bold text-brand-black">رسوم التوصيل</p>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      خدمة التوصيل تكلّفك <span className="font-bold text-burgundy-500">{formatPrice(deliveryFee)}</span> تُضاف إلى إجمالي الطلب.
                    </p>
                  </div>
                  <span className="hidden sm:inline-flex shrink-0 items-center rounded-full bg-burgundy-500 text-white px-3.5 py-1.5 text-sm font-bold font-cairo shadow-sm shadow-burgundy-500/20">
                    +{formatPrice(deliveryFee)}
                  </span>
                </div>
              </div>

              {measurableItems.length > 0 && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="flex items-center gap-2 text-xl font-tajawal font-bold">
                    <Ruler className="w-5 h-5 text-burgundy-500" />
                    قياساتك <span className="text-sm font-cairo font-normal text-red-500">*</span>
                  </h2>
                  <p className="font-cairo text-sm text-gray-500 mt-1 mb-4">
                    اكتبي الطول والوزن لكل قطعة عشان نتأكد إنه المقاس مناسب قبل الشحن.
                  </p>
                  <div className="divide-y divide-gray-100">
                    {measurableItems.map((item) => (
                      <div key={item.id} className="py-4 first:pt-0 last:pb-0 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div className="flex items-center gap-3 sm:w-56 shrink-0 min-w-0">
                          <ProductImage
                            src={item.image_url}
                            name={item.name_ar}
                            className="w-12 h-12 shrink-0 object-cover rounded"
                            textClassName="text-[9px]"
                          />
                          <div className="min-w-0 font-cairo">
                            <p className="font-semibold text-sm truncate">{item.name_ar}</p>
                            <p className="text-xs text-gray-500 truncate">
                              {[item.options_label, item.quantity > 1 && `× ${item.quantity}`].filter(Boolean).join(' · ')}
                            </p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3 flex-1">
                          <MeasureInput
                            id={`height-${item.id}`}
                            label="الطول"
                            unit="سم"
                            placeholder="مثلاً 165"
                            min={50}
                            max={250}
                            required
                            value={measurements[item.id]?.height ?? ''}
                            onChange={(v) => setMeasurement(item.id, 'height', v)}
                          />
                          <MeasureInput
                            id={`weight-${item.id}`}
                            label="الوزن"
                            unit="كجم"
                            placeholder="مثلاً 60"
                            min={20}
                            max={300}
                            required
                            value={measurements[item.id]?.weight ?? ''}
                            onChange={(v) => setMeasurement(item.id, 'weight', v)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-white rounded-lg shadow p-6">
                <Label htmlFor="order-notes" className="text-xl font-tajawal font-bold block mb-3">
                  ملاحظات الطلب <span className="text-sm font-cairo font-normal text-gray-500">(اختياري)</span>
                </Label>
                <Textarea
                  id="order-notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="مثلاً: التوصيل بعد العصر، أو علامة مميزة قرب البيت"
                  className="font-cairo"
                />
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-tajawal font-bold mb-4">طريقة الدفع</h2>
                <RadioGroup
                  data-testid={CHECKOUT.paymentMethod}
                  value={formData.payment_method}
                  onValueChange={(value) => setFormData({...formData, payment_method: value})}
                >
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <RadioGroupItem value="cod" id="cod" />
                    <Label htmlFor="cod" className="font-cairo">الدفع عند الاستلام</Label>
                  </div>
                </RadioGroup>
              </div>

              <div className="bg-white rounded-lg shadow p-6 border-2 border-burgundy-500/20">
                <h2 className="text-xl font-tajawal font-bold mb-2">خدمة المعاينة قبل الدفع</h2>
                <p className="text-sm text-gray-600 font-cairo mb-4">
                  تتيح لك خدمة المعاينة فحص المنتج قبل استلامه ودفع قيمته
                </p>
                <label className="flex items-start gap-3 cursor-pointer p-3 bg-brand-gold/5 rounded-lg hover:bg-brand-gold/10 transition-colors">
                  <input
                    type="checkbox"
                    data-testid="preview-service-checkbox"
                    checked={formData.preview_service_requested}
                    onChange={(e) => setFormData({...formData, preview_service_requested: e.target.checked})}
                    className="mt-1 w-5 h-5 accent-burgundy-500 cursor-pointer"
                  />
                  <div>
                    <span className="font-cairo font-semibold text-brand-black block">
                      أرغب بخدمة المعاينة عند التوصيل
                    </span>
                    <span className="text-sm text-gray-600 font-cairo">
                      سيتيح لك المندوب معاينة المنتج قبل الدفع النهائي
                    </span>
                  </div>
                </label>
              </div>

              <Button
                data-testid={CHECKOUT.submitBtn}
                type="submit"
                disabled={loading || limitReached}
                className="w-full bg-burgundy-500 hover:bg-burgundy-600 text-lg py-6"
              >
                {loading ? 'جاري إنشاء الطلب...' : 'تأكيد الطلب'}
              </Button>
            </form>
          </div>

          <div>
            <div className="bg-white rounded-lg shadow p-6 sticky top-24">
              <h2 className="text-xl font-tajawal font-bold mb-4">ملخص الطلب</h2>
              <div className="space-y-3 mb-6">
                {cart.items?.map((item) => (
                  <div key={item.id} className="flex gap-3 pb-3 border-b">
                    <ProductImage src={item.image_url} name={item.name_ar} className="w-16 h-16 shrink-0 object-cover rounded" textClassName="text-[10px]" />
                    <div className="flex-1 min-w-0">
                      <p className="font-cairo font-semibold text-sm">{item.name_ar}</p>
                      <p className="text-xs text-gray-500">{item.options_label}</p>
                      {/* سعر القطعة حسب التركيبة (اللون/الخصائص) × الكمية = الإجمالي */}
                      <div className="mt-1 flex items-center justify-between gap-2 font-cairo">
                        <p className="text-xs text-gray-500">
                          {formatPrice(item.price)} × {item.quantity}
                        </p>
                        <p className="text-sm font-bold text-burgundy-500 whitespace-nowrap">{formatPrice(item.line_total)}</p>
                      </div>
                      {!item.available && (
                        <p className="text-[11px] text-destructive font-cairo mt-0.5">غير متاح حالياً — لن يُحسب في الطلب</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="space-y-2 font-cairo">
                <div className="flex justify-between">
                  <span>المجموع الفرعي:</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span>رسوم التوصيل:</span>
                  <span>{formatPrice(deliveryFee)}</span>
                </div>
                <div className="flex justify-between font-bold text-lg border-t pt-2">
                  <span>الإجمالي:</span>
                  <span className="text-burgundy-500">{formatPrice(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isAddressModalOpen && (
        <AddressModal
          addressForm={addressForm}
          setAddressForm={setAddressForm}
          cities={cities}
          handleSaveAddress={handleSaveAddress}
          setIsAddressModalOpen={setIsAddressModalOpen}
          addressSubmitting={addressSubmitting}
        />
      )}
    </div>
  );
};

export default Checkout;
